import { Component, ChangeDetectionStrategy, OnInit, Inject, ChangeDetectorRef } from "@angular/core";
import { Evaluation, ReportSample, ReportTable, Sample, SampleType } from "@core/data";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormControl, FormGroup } from "@angular/forms";

export interface ReportTableEvaluationFormDialogData {
    data: Partial<ReportTable>;
    samples: ReportSample[];
}

@Component({
    selector: 'pv-report-table-evaluation-form-dialog',
    templateUrl: 'report-table-evaluation-form.dialog.html',
    styleUrls: ['report-table-evaluation-form.dialog.scss'],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportTableEvaluationFormDialog implements OnInit {

    constructor(
        @Inject(MAT_DIALOG_DATA) private _data: ReportTableEvaluationFormDialogData,
        private _ref: MatDialogRef<ReportTableEvaluationFormDialog>,
        private _cdr: ChangeDetectorRef,
    ) {}

    samples: ReportSample[] = [];
    table: Partial<ReportTable>;

    includeEvaluationControl = new FormControl([]);

    evalSearchControl = new FormControl('');

    SampleType = SampleType;

    private _filterText = '';

    ngOnInit() {
        this.table = this._data.data;
        this.samples = this._data.samples;

        this.includeEvaluationControl.setValue(this.table.tableOptions ? this.table.tableOptions.includedEvals : []);

        this.evalSearchControl.valueChanges
            .subscribe((value: string) => {
                this._filterText = (value || '').trim().toLowerCase();
                this._cdr.markForCheck();
            })
    }

    attempt() {
        const cast = {
            id: this._data.data.id,
            title: this.table.title,
            description: this.table.description,
            index: this.table.index,
            tableOptions: {
                columns: this.table.tableOptions.columns,
                isAggregate: this.table.tableOptions.isAggregate,
                isSpread: this.table.tableOptions.isSpread,
                showSummary: this.table.tableOptions.showSummary,
                includedEvals: this.includeEvaluationControl.value,
                cropId: this.table.tableOptions.cropId,
            },
        };

        this._ref.close(cast);
    }

    cancel() {
        this._ref.close();
    }

    hasVisibleEvals(): boolean {
        return this.samples.some(sample => this.visibleEvals(sample).length > 0);
    }

    matchesFilter(evalu: Evaluation): boolean {
        return !this._filterText || (evalu.label || '').toLowerCase().includes(this._filterText);
    }

    visibleEvals(sample: ReportSample): Evaluation[] {
        const evals = (sample.data && sample.data.evals) || [];
        return evals.filter(evalu => this.matchesFilter(evalu));
    }

    clearSearch() {
        this.evalSearchControl.setValue('');
    }

    // bulk actions only touch evals visible under the active search filter
    sampleAction(sampleType: SampleType, addAction: boolean) {
        const includedEvals: string[] = this.includeEvaluationControl.value || [];
        const targetEvals = new Set<string>();

        this.samples.forEach(sample => {
            if (!sample.data || sample.data.type !== sampleType) return;
            const visibleKeys = new Set(this.visibleEvals(sample).map(evalu => evalu.key));
            sample.includeEvals.forEach(key => {
                if (visibleKeys.has(key)) targetEvals.add(key);
            });
        });

        if (addAction) this.includeEvaluationControl.setValue([...new Set([...includedEvals, ...targetEvals])]);
        else this.includeEvaluationControl.setValue(includedEvals.filter(evalu => !targetEvals.has(evalu)));
    }
}
